import type { ReactNode } from "react";
import { cn } from "../../../lib/utils.js";
import { IsolatedBrands } from "../IsolatedBrands.js";

export type ProductStateTone = "empty" | "filtered" | "readonly" | "prompt";

const TONE_CLASS: Record<ProductStateTone, string> = {
  empty: "max-w-md gap-3 px-5 py-10",
  filtered: "max-w-sm gap-2 rounded-xl border border-dashed border-line bg-surface px-5 py-8",
  readonly: "max-w-md gap-2 px-5 py-8",
  prompt: "max-w-sm gap-2 px-4 py-8",
};

/**
 * Shared empty, filtered, read-only, and selection states.
 * Centered and compact so the workspace does not become a gray placeholder.
 */
export function ProductState({
  tone = "empty",
  visual,
  title,
  description,
  action,
  testId,
  className,
}: {
  tone?: ProductStateTone;
  visual?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  testId?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "mx-auto grid w-full justify-items-center text-center",
        TONE_CLASS[tone],
        className,
      )}
      data-testid={testId}
      data-state-tone={tone}
    >
      {visual ? <div className="text-ink-muted">{visual}</div> : null}
      <div className="grid max-w-sm gap-1.5">
        <h3 className="m-0 text-[0.9375rem] font-semibold tracking-[-0.01em] text-ink">{title}</h3>
        {description ? (
          <p className="m-0 text-[0.8125rem] leading-relaxed text-muted">
            {typeof description === "string" ? <IsolatedBrands text={description} /> : description}
          </p>
        ) : null}
      </div>
      {action ? <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
    </div>
  );
}
